/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { useLocation, useParams } from 'wouter';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FormPanel, Link, Button, FontFaces } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useParsedStory } from '@micromag/core/hooks';
import { ScreensProvider } from '@micromag/screens';

import { useStory } from '../../../hooks';
import MainLayout from '../../layouts/Main';
import Page from '../../partials/Page';
import StoryMenu from '../../menus/Story';
import ScreensList from '../../lists/Screens';

import useScreenContributions from '../../../hooks/useScreenContributions';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const ContributionsPage = ({ className }) => {
    const [pathname] = useLocation();
    const url = useUrlGenerator();
    const { story: storyId, screen: screenId } = useParams();
    const { story } = useStory(storyId);
    const { components = [], fonts = null } = useParsedStory(story) || {};
    const finalFonts = useMemo(() => fonts || [], [fonts]);
    const screen = components.find((s) => s.id === screenId) || null;

    const parent = story !== null ? story.title : null;
    const parentUrl = story !== null ? url('stories.participations', { story: story.id }) : null;

    const title = <FormattedMessage defaultMessage="Contributions" descrition="Page title" />;
    const nav = [
        { label: parent, url: parentUrl },
        { label: title, url: pathname },
    ];

    const { contributions, loading } = useScreenContributions(storyId, screenId);

    return (
        <MainLayout nav={nav}>
            <Page
                title={title}
                section={
                    <Link href={parentUrl} withoutStyle>
                        {parent}
                    </Link>
                }
                sidebar={story !== null ? <StoryMenu story={story} asList /> : <div />}
                className={classNames([
                    {
                        [className]: className !== null,
                    },
                ])}
            >
                <FontFaces fonts={finalFonts} />
                <ScreensProvider>
                    <FormPanel>
                        <Button
                            theme="secondary"
                            href={url('stories.participation', { story: storyId })}
                            icon={<FontAwesomeIcon icon={faAngleLeft} />}
                        >
                            <FormattedMessage
                                defaultMessage="Back to participation"
                                description="Contribution Label"
                            />
                        </Button>
                        <hr />
                        <h3>{title}</h3>
                        {story !== null && screen !== null ? (
                            <ScreensList
                                items={[screen]}
                                story={story}
                                itemLink="stories.contributions.show"
                                itemType="list"
                                withoutLink
                            />
                        ) : null}
                        <hr />
                        <table className="table table-sm">
                            <thead className="thead-dark">
                                <tr>
                                    <th scope="col">
                                        <FormattedMessage
                                            defaultMessage="Nom"
                                            description="Contribution Label"
                                        />
                                    </th>
                                    <th scope="col">
                                        <FormattedMessage
                                            defaultMessage="Message"
                                            description="Contribution Label"
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {contributions !== null
                                    ? contributions.map((c) => (
                                        <tr>
                                            <td className="w-25">{c.name}</td>
                                            <td>{c.message}</td>
                                        </tr>
                                    ))
                                    : null}
                                {contributions === null && !loading ? (
                                    <FormattedMessage
                                        defaultMessage="Could not find a contribution for this screen."
                                        description="Empty message"
                                    />
                                ) : null}
                            </tbody>
                        </table>
                    </FormPanel>
                </ScreensProvider>
            </Page>
        </MainLayout>
    );
};

ContributionsPage.propTypes = propTypes;
ContributionsPage.defaultProps = defaultProps;

export default ContributionsPage;
